import cx from 'classnames';
import { useCallback, useEffect, type FunctionComponent } from 'react';
import { useIntl } from 'react-intl';

import CloseIcon from '@atlaskit/icon/core/cross';
import { Analytics } from '@trello/atlassian-analytics';
import { RovoIcon } from '@trello/logo';
import { Button } from '@trello/nachos/button';
import { usePrefetchRovoChatInterface } from '@trello/rovo';
import { token } from '@trello/theme';

import * as styles from './RovoButton.module.less';

export interface RovoButtonProps {
  isActive: boolean;
  onClick: () => void;
  source: 'cardDetailScreen' | 'rovoIsland';
  workspaceId: string;
  isCardBackButton?: boolean;
}

export const RovoButton: FunctionComponent<RovoButtonProps> = ({
  isActive,
  onClick,
  source,
  workspaceId,
  isCardBackButton = false,
}) => {
  const intl = useIntl();
  const [, trackFeatureUsage] = usePrefetchRovoChatInterface();

  useEffect(() => {
    Analytics.sendUIEvent({
      action: 'rendered',
      actionSubject: 'button',
      actionSubjectId: 'rovoChatButton',
      source,
      attributes: {
        isCardBackButton,
        workspaceId,
      },
    });
  }, [source, isCardBackButton, workspaceId]);

  const handleClick = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'rovoChatButton',
      source,
      attributes: {
        action: isActive ? 'close' : 'open',
      },
    });

    // Record that the user opened Rovo chat so the
    // recently-used preloader knows to preload on future visits.
    if (!isActive) {
      trackFeatureUsage();
    }

    onClick();
  }, [onClick, source, isActive, trackFeatureUsage]);

  return (
    <>
      {!isActive && (
        <Button
          className={cx({
            [styles.rovoButton]: true,
            [styles.cardBackButton]: isCardBackButton,
          })}
          onClick={handleClick}
          aria-label={intl.formatMessage({
            id: 'templates.rovo-chat.open-rovo',
            defaultMessage: 'Open Rovo',
            description: 'Accessible label for the button that opens Rovo',
          })}
          iconBefore={
            <RovoIcon
              label="Rovo"
              appearance="brand"
              shouldUseHexLogo
              size="medium"
            />
          }
        />
      )}
      {isActive && (
        <Button
          className={cx({
            [styles.rovoButton]: true,
            [styles.cardBackButton]: isCardBackButton,
            [styles.chatOpen]: true,
          })}
          onClick={handleClick}
          aria-label={intl.formatMessage({
            id: 'templates.rovo-chat.close-rovo',
            defaultMessage: 'Close Rovo',
            description: 'Accessible label for the button that closes Rovo',
          })}
          iconBefore={
            <CloseIcon
              label={intl.formatMessage({
                id: 'templates.rovo-chat.close',
                defaultMessage: 'Close',
                description: 'Label for the close icon',
              })}
              color={token('color.icon.inverse', '#ffffff')}
            />
          }
        />
      )}
    </>
  );
};
